import { Component } from "react";
import { ApiKeyOrGetter } from "./GeneralService";
import { Observable } from "rxjs";
import { GeneralService } from "./GeneralService";

interface RequestAnalyticsData {
  timestamp: number;
  peak_requests_per_minute: number;
  avg_requests_per_minute: number;
}

interface FaultRateAnalyticsData {
  timestamp: number;
  total_requests: number;
  faulty_requests: number;
  fault_rate_percentage: number;
}

interface ApplicationAnalyticsResponse {
  data: RequestAnalyticsData[];
  period_days: number;
  total_requests: number;
  start_timestamp: number;
  end_timestamp: number;
}

interface FaultRateAnalyticsResponse {
  data: FaultRateAnalyticsData[];
  period_days: number;
  total_requests: number;
  total_faulty_requests: number;
  overall_fault_rate_percentage: number;
  start_timestamp: number;
  end_timestamp: number;
}

interface CompanyAnalyticsResponse {
  data: RequestAnalyticsData[];
  period_days: number;
  total_requests: number;
  start_timestamp: number;
  end_timestamp: number;
}

export class AnalyticsService extends Component {
  /**
   * Get request analytics for a specific application over a specified time period
   */
  public static getApplicationRequestAnalytics(
    applicationId: number,
    loadingHandler: any,
    apiKey: ApiKeyOrGetter,
    startDate?: string,
    endDate?: string
  ) {
    // Build query parameters
    const params = new URLSearchParams();
    if (startDate) {
      params.append("start_date", startDate);
    }
    if (endDate) {
      params.append("end_date", endDate);
    }
    
    const queryString = params.toString();
    const path = `/analytics/application/${applicationId}/requests${queryString ? `?${queryString}` : ""}`;
    
    return GeneralService.get(
      path,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get request analytics for all applications in the company over the last 14 days
   */
  public static getCompanyRequestAnalytics(
    loadingHandler: any,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      "/analytics/company/requests",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get request analytics for a specific application action over a specified time period
   */
  public static getApplicationActionRequestAnalytics(
    applicationId: number,
    actionId: number,
    loadingHandler: any,
    apiKey: ApiKeyOrGetter,
    startDate?: string,
    endDate?: string
  ) {
    // Build query parameters
    const params = new URLSearchParams();
    if (startDate) {
      params.append("start_date", startDate);
    }
    if (endDate) {
      params.append("end_date", endDate);
    }
    
    const queryString = params.toString();
    const path = `/analytics/application/${applicationId}/intent/${actionId}/requests${queryString ? `?${queryString}` : ""}`;
    
    return GeneralService.get(
      path,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get request analytics for a specific intent group over a specified time period
   */
  public static getIntentGroupRequestAnalytics(
    intentGroupId: number,
    loadingHandler: any,
    apiKey: ApiKeyOrGetter,
    startDate?: string,
    endDate?: string
  ) {
    // Build query parameters
    const params = new URLSearchParams();
    if (startDate) {
      params.append("start_date", startDate);
    }
    if (endDate) {
      params.append("end_date", endDate);
    }
    
    const queryString = params.toString();
    const path = `/analytics/intent-group/${intentGroupId}/requests${queryString ? `?${queryString}` : ""}`;
    
    return GeneralService.get(
      path,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get fault rate analytics for a specific application over a specified time period
   */
  public static getApplicationFaultRateAnalytics(
    applicationId: number,
    loadingHandler: any,
    apiKey: ApiKeyOrGetter,
    startDate?: string,
    endDate?: string
  ) {
    // Build query parameters
    const params = new URLSearchParams();
    if (startDate) {
      params.append("start_date", startDate);
    }
    if (endDate) {
      params.append("end_date", endDate);
    }
    
    const queryString = params.toString();
    const path = `/analytics/application/${applicationId}/fault-rate${queryString ? `?${queryString}` : ""}`;
    
    return GeneralService.get(
      path,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get fault rate analytics for a specific application action over a specified time period
   */
  public static getApplicationActionFaultRateAnalytics(
    applicationId: number,
    actionId: number,
    loadingHandler: any,
    apiKey: ApiKeyOrGetter,
    startDate?: string,
    endDate?: string
  ) {
    // Build query parameters
    const params = new URLSearchParams();
    if (startDate) {
      params.append("start_date", startDate);
    }
    if (endDate) {
      params.append("end_date", endDate);
    }
    
    const queryString = params.toString();
    const path = `/analytics/application/${applicationId}/intent/${actionId}/fault-rate${queryString ? `?${queryString}` : ""}`;
    
    return GeneralService.get(
      path,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get request analytics for a specific agent over a specified time period
   */
  public static getAgentRequestAnalytics(
    agentId: number,
    loadingHandler: any,
    apiKey: ApiKeyOrGetter,
    startDate?: string,
    endDate?: string
  ) {
    // Build query parameters
    const params = new URLSearchParams();
    if (startDate) {
      params.append("start_date", startDate);
    }
    if (endDate) {
      params.append("end_date", endDate);
    }

    const queryString = params.toString();
    const url = `/analytics/agent/${agentId}/requests${
      queryString ? `?${queryString}` : ""
    }`;

    return GeneralService.get(
      url,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    ) as Observable<ApplicationAnalyticsResponse>;
  }

  /**
   * Get fault rate analytics for a specific intent group over a specified time period
   */
  public static getIntentGroupFaultRateAnalytics(
    intentGroupId: number,
    loadingHandler: any,
    apiKey: ApiKeyOrGetter,
    startDate?: string,
    endDate?: string
  ) {
    // Build query parameters
    const params = new URLSearchParams();
    if (startDate) {
      params.append("start_date", startDate);
    }
    if (endDate) {
      params.append("end_date", endDate);
    }
    
    const queryString = params.toString();
    const path = `/analytics/intent-group/${intentGroupId}/fault-rate${queryString ? `?${queryString}` : ""}`;
    
    return GeneralService.get(
      path,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
}

export type { RequestAnalyticsData, FaultRateAnalyticsData, ApplicationAnalyticsResponse, FaultRateAnalyticsResponse, CompanyAnalyticsResponse };